import React, { useState } from 'react';
import { User } from '../types/User';
import { Eye, EyeOff } from 'lucide-react';
import { AuthService } from '../services/authService';

interface LoginPageProps {
  onLogin: (user: User) => void;
  onNavigateToRegister?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onNavigateToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    // Login with Supabase
    AuthService.login(email, password).then(({ user, error }) => {
      if (error) {
        setError(error);
      } else if (user) {
        onLogin(user);
      }
      setIsLoading(false);
    });
  };

  const handlePasswordReset = async () => {
    setError('');
    setResetMessage('');

    if (!resetEmail.trim()) {
      setError('Enter your email address');
      return;
    }

    setIsLoading(true);
    const { error } = await AuthService.requestPasswordReset(resetEmail);
    setIsLoading(false);

    if (error) {
      setError(error);
      return;
    }

    setResetMessage('Password reset email sent. Check your inbox and spam folder.');
    setShowForgotPassword(false);
    setResetEmail('');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://events.logisticsmarket.rs/wp-content/uploads/2025/09/LM-layout.png')`
      }}
    >
      <div className="absolute inset-0 bg-white bg-opacity-60"></div>
      <div className="absolute inset-0" style={{ backgroundColor: 'rgb(0 0 0 / 40%)' }}></div>
      
      <div className="relative z-10 bg-white rounded-lg shadow-2xl p-8 w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <img 
            src="https://events.logisticsmarket.rs/wp-content/uploads/2022/02/Logistics-Market-logo-6.webp"
            alt="Logistics Market Logo"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Networking app</h1>
          <p className="text-sm text-gray-600">LOGISTICS MARKET EXPO & CONFERENCE 2026</p>
          <p className="text-xs text-gray-500 mt-1">6 October 2026 · Beogradski sajam, Hala 5</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {resetMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {resetMessage}
            </div>
          )}

          {showForgotPassword && (
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-md">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Reset Password</h3>
              <div className="space-y-3">
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:border-yellow-600"
                  placeholder="Enter your email address"
                  disabled={isLoading}
                />
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={isLoading}
                    className="flex-1 bg-yellow-600 text-white py-2 px-4 rounded-md font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition duration-200"
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Email'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md font-semibold hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:border-yellow-600"
              placeholder="your.email@company.com"
              disabled={isLoading}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-600 focus:border-yellow-600"
                placeholder="••••••••••"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Remember me
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full text-white py-3 px-4 rounded-md font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition duration-200"
            style={{ backgroundColor: '#C89116' }}
          >
            {isLoading ? 'LOGGING IN...' : 'LOGIN'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(!showForgotPassword)}
              className="text-sm hover:opacity-80 underline"
              style={{ color: '#C89116' }}
              disabled={isLoading}
            >
              Forgot password?
            </button>
          </div>


          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">Don't have an account?</p>
            <button
              type="button"
              onClick={() => {
                if (onNavigateToRegister) {
                  onNavigateToRegister();
                }
              }}
              className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-md font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200"
            >
              CREATE NEW ACCOUNT
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;